// Shared FE ports of the Device UI IPv4 helpers used by the VPN families (R11 overview 6.6 owns the
// export names; R11a, R11b, R11c, R11d and R11e use exactly these names). Each port matches the
// Device UI function exactly, quirks included (rules R1 / R1a): do not anchor, widen or narrow a
// pattern. The only change is the rule R1 type exception: the input is the field's text, and
// `undefined`, `null` or a non-string returns the answer that blocks the save. The file holds no
// message ids; each family maps a result to its own id.
// Device UI: DUI:Utils/validates/Validations.js at 017ef4f3d.
// R11a created this file with the exports it uses; later VPN features add the other ports of 6.6.

// DUI isValidateIPformat (Validations.js 15-20), expressed without its backtracking regex.
// The Device UI accepts one-to-three decimal digits per octet (including leading zeroes), rejects
// values above 255, and rejects only the exact text 0.0.0.0.
const isDuiIpv4Text = (value) => {
  const parts = value.split('.');
  return parts.length === 4 && parts.every((part) => /^[0-9]{1,3}$/.test(part) && Number(part) <= 255);
};

// DUI isValidationFQDNformat (Validations.js 31-36), exact regex: labels may hold `_ . - @`, no dot required.
const DUI_FQDN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9_.\-@]*[a-zA-Z0-9])?)(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9_.\-@]*[a-zA-Z0-9])?)*$/;

// DUI validateSubnetIP (Validations.js 167-174), exact regex: end anchor only, no start anchor (kept).
const DUI_SUBNET_REGEX =
  /(?!0\.0\.0\.0$)(((255\.){3}(255|254|252|248|240|224|192|128|0+))|((255\.){2}(255|254|252|248|240|224|192|128|0+)\.0)|((255\.)(255|254|252|248|240|224|192|128|0+)(\.0+){2})|((255|254|252|248|240|224|192|128|0+)(\.0+){3}))$/;

// Stands in for the Device UI octet boxes (four inputs, maxLength 3); adds no rule of its own.
const FOUR_OCTET_SHAPE_REGEX = /^[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}$/;

const INTEGER_TEXT_REGEX = /^[0-9]+$/;

/** Port of isValidateIPformat. `true` = invalid. Rejects only the exact text `0.0.0.0`; leading zeros pass. */
export const isPrIpv4FormatInvalid = (value) => {
  if (typeof value !== 'string') return true;
  return value === '0.0.0.0' || !isDuiIpv4Text(value);
};

/**
 * Port of isValidationFQDNformat. `true` = invalid. One label passes (`peer`), `a..b` passes, `_` and `@`
 * are allowed inside a label, any space is invalid (quirks kept, R11b S2S-V3).
 */
export const isPrFqdnFormatInvalid = (value) => {
  if (typeof value !== 'string') return true;
  return !DUI_FQDN_REGEX.test(value) || value.indexOf(' ') >= 0;
};

/** Port of validateSubnetIP. `true` = valid. No start anchor, so `100.0.0.0` and `00.0.0.0` pass (quirk kept). */
export const isPrSubnetMaskValid = (value) => {
  if (typeof value !== 'string') return false;
  return DUI_SUBNET_REGEX.test(value);
};

/** `true` = four dot groups of 1-3 digits (the Device UI octet boxes). Callers run it before isPrSubnetMaskValid. */
export const hasPrIpv4FourOctetShape = (value) => {
  if (typeof value !== 'string') return false;
  return FOUR_OCTET_SHAPE_REGEX.test(value);
};

/**
 * Rule R1 type exception (Confluence types win over a Device UI NaN / fraction): `true` = digits only.
 * A family rejects `false` with the field's required (blank) or range message.
 */
export const isPrIntegerText = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value !== 'string' && typeof value !== 'number') return false;
  return INTEGER_TEXT_REGEX.test(String(value));
};

// R11d adds the ports below (R11 overview 6.6 names; DUI:Utils/validates/Validations.js and
// DUI:Utils/NetworkStart.js at 017ef4f3d).

/**
 * Port of isBroadcastIp (Validations.js 22-30). `true` = broadcast-like: octet 2, 3 or 4 is `255`, or the
 * text is `255.255.255.255`; the first octet is not checked (quirk kept). A non-string blocks (`true`).
 */
export const isPrBroadcastLikeIpv4 = (value) => {
  if (typeof value !== 'string') return true;
  const octets = value.split('.').map(Number);
  return octets[3] === 255 || octets[2] === 255 || octets[1] === 255 || value === '255.255.255.255';
};

/**
 * Port of validateIPendswith255 (Validations.js 224-228). `true` = the fourth dot group is exactly the text `255`. A
 * non-string is `false`; callers check the address format first.
 */
export const isPrIpv4EndsWith255 = (value) => typeof value === 'string' && value.split('.')[3] === '255';

/**
 * Port of validatebroadcastIP (Validations.js 195-199). `true` = the first dot group is a number from 224 to 255
 * (multicast, reserved and broadcast space). A non-string is `false`; callers check the address format first.
 */
export const isPrIpv4FirstOctetBroadcast = (value) => {
  if (typeof value !== 'string') return false;
  const first = value.split('.')[0];
  return first >= 224 && first <= 255;
};

// DUI ipToBinary (NetworkStart.js 1-7): Number(octet).toString(2) per dot group.
const toDuiBinaryGroups = (value) =>
  value
    .split('.')
    .map(Number)
    .map((num) => num.toString(2).padStart(8, '0'));

const countOneBits = (value) =>
  toDuiBinaryGroups(value)
    .join('')
    .split('')
    .filter((bit) => bit === '1').length;

/** Port of isNetmaskGreaterThan29 (NetworkStart.js 28-32): more than 29 one bits, also in a non-contiguous mask. */
export const isPrNetmaskGreaterThan29 = (mask) => {
  if (typeof mask !== 'string') return false;
  return countOneBits(mask) > 29;
};

/** Port of isNetmaskEqualTo32 (NetworkStart.js 33-37). */
export const isPrNetmaskEqualTo32 = (mask) => {
  if (typeof mask !== 'string') return false;
  return countOneBits(mask) === 32;
};

const toIpv4Octets = (value) => {
  if (typeof value !== 'string') return null;
  const parts = value.split('.');
  if (parts.length !== 4) return null;
  const octets = parts.map((part) => (/^[0-9]{1,3}$/.test(part) ? Number(part) : NaN));
  return octets.some((octet) => Number.isNaN(octet) || octet > 255) ? null : octets;
};

/**
 * Port of calculateNetworkAddress (NetworkStart.js 14-22): the bitwise AND of address and mask, octet by
 * octet. `null` when either input is not four dot groups of 0-255 (the Device UI would compare NaN there).
 */
export const getPrIpv4NetworkAddress = (ip, mask) => {
  const ipOctets = toIpv4Octets(ip);
  const maskOctets = toIpv4Octets(mask);
  if (!ipOctets || !maskOctets) return null;
  return ipOctets.map((octet, index) => octet & maskOctets[index]).join('.');
};

const DUI_HOSTNAME_LABEL_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

/**
 * Port of isValidHostname (Validations.js 62-73). `true` = valid: at most 253 characters and every dot
 * label matches the label pattern, so `192.168.1.5` and `999.1.1.1` pass and `_` fails (quirk kept).
 */
export const isPrHostnameLike = (value) => {
  if (!value || typeof value !== 'string') return false;
  if (value.length > 253) return false;
  return value.split('.').every((label) => DUI_HOSTNAME_LABEL_REGEX.test(label));
};
